import LoadingState from 'components/LoadingState';
import { ColumnService } from 'lib/services/directory';
import { useEffect } from 'react';
import {
  isRouteErrorResponse,
  useLocation,
  useRouteError
} from 'react-router-dom';
import { selectAuthLoading, selectUserAuth } from 'redux/auth';
import { useAppSelector } from 'redux/hooks';
import { logAndCaptureException } from 'utils';
import { useRedirectToLanding, useRedirectToLogin } from './redirect/hooks';

export function UnknownRoute() {
  const { pathname } = useLocation();
  // Any error caught by React Router will be available here
  const error = useRouteError();
  const is404Error = isRouteErrorResponse(error) && error.status === 404;

  const redirectToLanding = useRedirectToLanding();
  const redirectToLogin = useRedirectToLogin();
  const auth = useAppSelector(selectUserAuth);
  const authLoading = useAppSelector(selectAuthLoading);

  useEffect(() => {
    // A 404 error is not really an "error" in the sense of a bug, but we still want to log it for analytics purposes
    if (is404Error) {
      logAndCaptureException(
        ColumnService.FRONTEND_ROUTING,
        new Error('Page not found'),
        'Unknown page requested',
        { pathname }
      );
    } else {
      // If the error is something else, we want to log it as an actual error for debugging purposes
      logAndCaptureException(
        ColumnService.UNKNOWN,
        error,
        'Error caught by React Router',
        { pathname }
      );
    }
  }, [pathname]);

  useEffect(() => {
    if (authLoading || !is404Error) {
      return;
    }

    // Once auth is loaded, redirect to the landing page or login page
    if (auth) {
      redirectToLanding();
    } else {
      redirectToLogin();
    }
  }, [auth, authLoading]);

  return (
    <LoadingState
      context={{ location: pathname, service: ColumnService.FRONTEND_ROUTING }}
    />
  );
}
